/**
 * TDS helpers — normalize API records, SEO slugs, and page meta.
 * Data comes from the polymer TDS API (see apiService.getPolymerTdsList).
 */

const API_BASE = 'https://backend.bsbinfotrade.com';
const PRIME_FILE_BASE = (
  process.env.REACT_APP_OBF_FILE_BASE || 'https://bsbprime.bsbinfotrade.com'
).replace(/\/$/, '');

export const SITE_ORIGIN = (
  process.env.REACT_APP_SITE_ORIGIN || 'https://www.bsbfortrade.com'
).replace(/\/$/, '');

export const getPolymerFamily = (gradeName = '') => {
  const name = String(gradeName).toUpperCase().trim();
  if (name.includes('PVC') || name.includes('POLYVINYL')) return 'PVC';
  if (name.includes('PP') || name.includes('POLYPROPYLENE')) return 'PP';
  if (name.includes('HDPE')) return 'HDPE';
  if (name.includes('LDPE') && !name.includes('LLDPE')) return 'LDPE';
  if (name.includes('LLDPE')) return 'LLDPE';
  if (name.includes('GPPS')) return 'GPPS';
  if (name.includes('HIPS')) return 'HIPS';
  if (name.includes('PET')) return 'PET';
  if (name.includes('ABS')) return 'ABS';
  return 'Polymer';
};

const pick = (item, keys, fallback = '') => {
  for (const key of keys) {
    const val = item?.[key];
    if (val !== undefined && val !== null && String(val).trim() !== '') {
      return String(val).trim();
    }
  }
  return fallback;
};

/** Resolve PDF URL — local /assets paths or BSB Prime admin uploads (public/...) */
export const resolveTdsPdfUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  if (trimmed.startsWith('/assets/') || trimmed.startsWith('/static/')) {
    return trimmed;
  }
  if (trimmed.startsWith('assets/')) {
    return `/${trimmed}`;
  }
  if (trimmed.startsWith('public/') || trimmed.startsWith('admin/')) {
    const path = trimmed.startsWith('public/') ? trimmed : `public/${trimmed}`;
    return `${PRIME_FILE_BASE}/${path}`;
  }
  const base = (process.env.REACT_APP_API_FILE_BASE || API_BASE).replace(/\/$/, '');
  return trimmed.startsWith('/') ? `${base}${trimmed}` : `${base}/${trimmed}`;
};

/** Dev-only: CRA setupProxy serves /obf-files. Live site must use direct PDF URLs. */
export const shouldUseObfPdfProxy = () =>
  process.env.NODE_ENV === 'development' ||
  process.env.REACT_APP_OBF_PDF_PROXY === 'true';

/** URL for fetch/embed — proxy in dev; direct bsbprime URL on production */
export const getTdsPdfAccessUrl = (pdfUrl) => {
  if (!pdfUrl) return '';
  if (!shouldUseObfPdfProxy()) return pdfUrl;
  if (pdfUrl.startsWith('/obf-files')) return pdfUrl;
  if (pdfUrl.includes('bsbprime.bsbinfotrade.com')) {
    return pdfUrl.replace(/^https?:\/\/bsbprime\.bsbinfotrade\.com/i, '/obf-files');
  }
  if (pdfUrl.startsWith(PRIME_FILE_BASE)) {
    return pdfUrl.replace(PRIME_FILE_BASE, '/obf-files');
  }
  return pdfUrl;
};

/** Load PDF bytes for in-modal preview (blob URL avoids SPA iframe on /obf-files) */
export const fetchTdsPdfBlobUrl = async (pdfUrl) => {
  if (!pdfUrl) throw new Error('no-url');
  const fetchUrl = getTdsPdfAccessUrl(pdfUrl);
  const response = await fetch(fetchUrl, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  if (!blob.size) throw new Error('Empty PDF');
  const type = blob.type && blob.type !== 'application/octet-stream' ? blob.type : 'application/pdf';
  return URL.createObjectURL(new Blob([blob], { type }));
};

/** Parse MFI / K-value from API row (empty when not provided) */
export const extractMfiValue = (item = {}) => {
  const raw = pick(item, [
    'mfi_value',
    'grade_code_mfi_value',
    'mfi',
    'k_value',
    'kvalue',
    'k_value_name',
    'mfi_val',
  ]);
  if (!raw) return '';
  const text = String(raw).trim();
  if (!text || /^n\/?a$/i.test(text) || text === '-') return '';
  return text;
};

const normMfiKeyPart = (value) => String(value || '').trim().toUpperCase();

export const buildMfiLookupKey = ({ codeName = '', gradeName = '', processName = '', brandName = '' } = {}) => {
  const code = normMfiKeyPart(codeName);
  const grade = normMfiKeyPart(gradeName);
  const process = normMfiKeyPart(processName);
  const brand = normMfiKeyPart(brandName);
  if (code && grade && process && brand) return `${code}|${grade}|${process}|${brand}`;
  if (code && grade && process) return `${code}|${grade}|${process}`;
  if (code) return code;
  return '';
};

export const resolveMfiForRow = (row = {}, lookupMap = null) => {
  const direct = extractMfiValue(row);
  if (direct) return direct;
  if (!lookupMap || typeof lookupMap.get !== 'function') return '';

  const codeName = row.grade_code_name || row.code_name || row.grade_code || row.code || '';
  const keys = [
    buildMfiLookupKey({
      codeName,
      gradeName: row.grade_name || row.grade,
      processName: row.process_name || row.process,
      brandName: row.brand_name || row.brand,
    }),
    buildMfiLookupKey({ codeName }),
  ];

  for (const key of keys) {
    if (key && lookupMap.has(key)) return lookupMap.get(key);
  }
  return '';
};

const TDS_VIEW_COUNT_KEY = 'bsb_tds_view_counts';

const localeCompareAsc = (a = '', b = '') =>
  String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

/** Track View clicks (per browser) for popular-first sorting */
export const recordTdsView = (slug) => {
  if (!slug || typeof window === 'undefined') return;
  try {
    const counts = JSON.parse(window.localStorage.getItem(TDS_VIEW_COUNT_KEY) || '{}');
    counts[slug] = (counts[slug] || 0) + 1;
    window.localStorage.setItem(TDS_VIEW_COUNT_KEY, JSON.stringify(counts));
  } catch (_) {
    /* ignore storage errors */
  }
};

export const getTdsViewCount = (slug) => {
  if (!slug || typeof window === 'undefined') return 0;
  try {
    const counts = JSON.parse(window.localStorage.getItem(TDS_VIEW_COUNT_KEY) || '{}');
    return Number(counts[slug]) || 0;
  } catch (_) {
    return 0;
  }
};

/** Group 0 = PDF available (top), group 1 = no PDF (bottom); then grade → brand → code */
const pdfSortGroup = (record) => (record?.hasPdf ? 0 : 1);

export const sortTdsCatalogRecords = (list = []) =>
  [...list].sort((a, b) => {
    const groupDiff = pdfSortGroup(a) - pdfSortGroup(b);
    if (groupDiff !== 0) return groupDiff;

    const gradeCmp = localeCompareAsc(a.gradeName, b.gradeName);
    if (gradeCmp !== 0) return gradeCmp;

    const brandCmp = localeCompareAsc(a.brandName, b.brandName);
    if (brandCmp !== 0) return brandCmp;

    return localeCompareAsc(a.codeName, b.codeName);
  });

/** Split sorted list: all with PDF first, all without PDF last */
export const partitionTdsByPdf = (list = []) => {
  const sorted = sortTdsCatalogRecords(list);
  return {
    withPdf: sorted.filter((r) => r.hasPdf),
    withoutPdf: sorted.filter((r) => !r.hasPdf),
  };
};

/** Remove trailing dot from process labels (e.g. Susp. → Susp) */
export const formatProcessName = (name = '') => {
  const text = String(name || '').trim();
  if (!text || text === 'N/A') return text;
  return text.replace(/\.$/, '');
};

/** Normalize one API row into a consistent frontend shape */
export const normalizeTdsRecord = (item = {}) => {
  const codeName = pick(item, ['code_name', 'grade_code_name', 'grade_code', 'code']);
  const gradeName = pick(item, ['grade_name', 'grade', 'polymer_grade']);
  const processName = formatProcessName(pick(item, ['process_name', 'process', 'process_type']));
  const brandName = pick(item, ['brand_name', 'brand', 'manufacturer']);
  const mfiRaw = extractMfiValue(item);
  const mfiValue = mfiRaw || 'N/A';
  const content = pick(item, ['content', 'description', 'tds_content', 'details', 'summary']);
  const rawPdfPath = pick(item, ['tds_url', 'tds_pdf', 'tds_pdf_url', 'pdf_url', 'pdf', 'document_url', 'file_url']);
  const pdfUrl = resolveTdsPdfUrl(rawPdfPath);
  const id = pick(item, ['grade_code_id', 'id', 'tds_id', 'grade_id'], codeName);
  const polymerFamily = getPolymerFamily(gradeName);
  const slug = buildTdsSlug({ gradeName, processName, brandName, codeName });
  const hasPdf = Boolean(pdfUrl);

  return {
    id,
    codeName,
    gradeName,
    processName,
    brandName,
    mfiValue,
    content,
    pdfUrl,
    polymerFamily,
    slug,
    hasPdf,
    raw: item,
  };
};

export const getTdsRecordKey = (item = {}) => {
  const codeId = item.raw?.grade_code_id ?? item.grade_code_id ?? item.id;
  if (codeId !== undefined && codeId !== null && String(codeId).trim() !== '') {
    return `GC:${String(codeId).trim()}`;
  }
  return [item.codeName, item.gradeName, item.processName, item.brandName]
    .map((v) => String(v || '').trim().toUpperCase())
    .filter(Boolean)
    .join('|');
};

/** SEO slug e.g. pvc-susp-hygain-sg5-tds-pdf */
export const buildTdsSlug = ({ gradeName = '', processName = '', brandName = '', codeName = '' } = {}) => {
  const parts = [gradeName, processName, brandName, codeName, 'tds', 'pdf'].filter(Boolean);
  return parts
    .join(' ')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

export const findTdsBySlug = (list, slugParam) => {
  if (!slugParam || !Array.isArray(list)) return null;
  const raw = decodeURIComponent(String(slugParam)).trim();
  const normalized = raw.toLowerCase();

  return (
    list.find((r) => r.slug === normalized) ||
    list.find((r) => r.codeName.toLowerCase() === normalized) ||
    list.find((r) => buildTdsSlug(r) === normalized) ||
    null
  );
};

export const getTdsListTitle = (record) => {
  if (!record) return 'Polymer TDS';
  const process = record.processName ? ` ${record.processName}` : '';
  return `${record.gradeName}${process} ${record.codeName} TDS PDF`.replace(/\s+/g, ' ').trim();
};

export const getTdsSeoDescription = (record) => {
  if (!record) {
    return 'Download polymer Technical Data Sheets (TDS) PDF for PVC, PP, HDPE, LDPE, LLDPE and more from BSB Fortrade.';
  }
  const excerpt = record.content
    ? `${record.content.slice(0, 140)}…`
    : `Download the official TDS PDF for ${record.brandName} ${record.codeName} (${record.gradeName}, ${record.processName}). MFI/K-Value: ${record.mfiValue}.`;
  return `${getTdsListTitle(record)} — ${excerpt} Available from BSB Fortrade, India.`;
};

/** Search phrases users type (e.g. "pvc susp tds pdf") */
export const getTdsSeoKeywords = (record) => {
  if (!record) {
    return 'polymer tds pdf, pvc tds pdf, pp tds pdf, hdpe tds pdf, technical data sheet download, bsb fortrade';
  }
  const grade = String(record.gradeName || '').toLowerCase();
  const process = String(record.processName || '').toLowerCase();
  const brand = String(record.brandName || '').toLowerCase();
  const code = String(record.codeName || '').toLowerCase();
  const parts = [
    `${grade} ${process} tds pdf`.trim(),
    `${grade} tds pdf`,
    `${code} tds pdf`,
    `${brand} ${code} tds`,
    'polymer tds download',
    'technical data sheet pdf',
    'bsb fortrade tds',
  ].filter(Boolean);
  return [...new Set(parts)].join(', ');
};

export const getTdsDetailPath = (record) => (record?.slug ? `/tds/${record.slug}` : '/tds');

export const getTdsDetailUrl = (record) => `${SITE_ORIGIN}${getTdsDetailPath(record)}`;

const setMeta = (name, content, attr = 'name') => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (path) => {
  const href =
    typeof window !== 'undefined'
      ? `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`
      : path;
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
};

const setJsonLd = (data) => {
  const id = 'tds-json-ld';
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
};

const setLinkRel = (rel, href, extra = {}) => {
  if (!href) return;
  const selector = `link[rel="${rel}"]${extra.type ? `[type="${extra.type}"]` : ''}`;
  let link = document.querySelector(selector);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
  if (extra.type) link.type = extra.type;
  if (extra.title) link.title = extra.title;
};

const clearLinkRel = (rel, type) => {
  const selector = type ? `link[rel="${rel}"][type="${type}"]` : `link[rel="${rel}"]`;
  document.querySelectorAll(selector).forEach((el) => el.remove());
};

export const applyTdsCatalogSeo = () => {
  const path = '/tds';
  const pageUrl = `${SITE_ORIGIN}${path}`;
  document.title = 'All Polymer TDS — Download Technical Data Sheets PDF | BSB Fortrade';
  setMeta(
    'description',
    'Download polymer TDS PDF — PVC Susp, PP Injection, HDPE, LLDPE and more. Search grades, view and download technical data sheets from BSB Fortrade India.'
  );
  setMeta(
    'keywords',
    'polymer tds pdf, pvc susp tds pdf, pp injection tds pdf, hdpe tds pdf, technical data sheet download, bsb fortrade tds'
  );
  setMeta('robots', 'index, follow');
  setCanonical(path);
  setMeta('og:title', document.title, 'property');
  setMeta('og:description', getTdsSeoDescription(), 'property');
  setMeta('og:url', pageUrl, 'property');
  setMeta('og:type', 'website', 'property');
  clearLinkRel('alternate', 'application/pdf');
  setJsonLd({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'All Polymer TDS — BSB Fortrade',
    description: 'Technical Data Sheets (TDS) PDF library for prime polymer grades.',
    url: pageUrl,
    publisher: {
      '@type': 'Organization',
      name: 'BSB Fortrade',
      url: SITE_ORIGIN,
    },
  });
};

export const applyTdsKeywordLandingSeo = (landing) => {
  if (!landing) return;
  document.title = landing.title;
  setMeta('description', landing.description);
  setMeta('keywords', landing.keywords);
  setMeta('robots', 'index, follow');
  setCanonical(landing.path);
  const pageUrl = `${SITE_ORIGIN}${landing.path}`;
  setMeta('og:title', landing.title, 'property');
  setMeta('og:description', landing.description, 'property');
  setMeta('og:url', pageUrl, 'property');
  setMeta('og:type', 'website', 'property');
  clearLinkRel('alternate', 'application/pdf');
  setJsonLd({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: landing.title,
    description: landing.description,
    url: pageUrl,
    keywords: landing.keywords,
    publisher: { '@type': 'Organization', name: 'BSB Fortrade', url: SITE_ORIGIN },
  });
};

export const applyTdsDetailSeo = (record) => {
  if (!record) return;
  const path = getTdsDetailPath(record);
  const pageUrl = getTdsDetailUrl(record);
  const title = `${getTdsListTitle(record)} | BSB Fortrade`;
  const description = getTdsSeoDescription(record);
  const keywords = getTdsSeoKeywords(record);

  document.title = title;
  setMeta('description', description);
  setMeta('keywords', keywords);
  setMeta('robots', 'index, follow');
  setCanonical(path);
  setMeta('og:title', title, 'property');
  setMeta('og:description', description, 'property');
  setMeta('og:url', pageUrl, 'property');
  setMeta('og:type', record.hasPdf ? 'article' : 'website', 'property');

  const pdfUrl = record.pdfUrl;
  if (pdfUrl) {
    setLinkRel('alternate', pdfUrl, { type: 'application/pdf', title: getTdsListTitle(record) });
  } else {
    clearLinkRel('alternate', 'application/pdf');
  }

  setJsonLd({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: title,
        description: record.content || description,
        url: pageUrl,
        inLanguage: 'en-IN',
        isPartOf: { '@type': 'WebSite', name: 'BSB Fortrade', url: SITE_ORIGIN },
      },
      {
        '@type': 'Product',
        name: `${record.gradeName} ${record.codeName}`,
        brand: { '@type': 'Brand', name: record.brandName },
        sku: record.codeName,
        category: record.polymerFamily || 'Polymer',
      },
      ...(pdfUrl
        ? [
            {
              '@type': 'DigitalDocument',
              name: getTdsListTitle(record),
              description,
              encodingFormat: 'application/pdf',
              url: pdfUrl,
              contentUrl: pdfUrl,
              publisher: { '@type': 'Organization', name: 'BSB Fortrade', url: SITE_ORIGIN },
            },
          ]
        : []),
    ],
  });
};

/** Download PDF (blob fetch) with fallback open in new tab */
export const downloadTdsPdf = async (record) => {
  if (!record?.pdfUrl) return { ok: false, reason: 'no-url' };

  const fileName = `${record.codeName || record.slug || 'polymer'}-tds.pdf`.replace(/[^a-z0-9.-]+/gi, '-');
  const fetchUrl = shouldUseObfPdfProxy() ? getTdsPdfAccessUrl(record.pdfUrl) : record.pdfUrl;
  const isSiteAsset =
    fetchUrl.startsWith('/assets/') || fetchUrl.startsWith('/static/');

  if (isSiteAsset) {
    const link = document.createElement('a');
    link.href = fetchUrl;
    link.download = fileName;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return { ok: true };
  }

  try {
    const response = await fetch(fetchUrl, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const blob = await response.blob();
    if (!blob.size) {
      throw new Error('Empty PDF response');
    }
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    return { ok: true };
  } catch (err) {
    console.warn('TDS PDF download via fetch failed:', err);
    window.open(record.pdfUrl, '_blank', 'noopener,noreferrer');
    return { ok: true, fallback: true };
  }
};

export const openTdsPdfInNewTab = (record) => {
  if (!record?.pdfUrl) return;
  window.open(record.pdfUrl, '_blank', 'noopener,noreferrer');
};

/** @deprecated use downloadTdsPdf */
export const triggerPdfDownload = (record) => {
  downloadTdsPdf(record);
  return true;
};
